/**
 * Administração → WhatsBI Cockpit: o modelo de linguagem da concessionária e o que ele
 * consumiu (ADR-044).
 *
 * **A chave.** Cada organização cola a chave da OpenAI **dela**; sem ela, vale a da
 * instalação (o `.env` da VM); sem nenhuma, as perguntas são entendidas pelas regras. A tela
 * diz qual está em uso, e o botão *Testar* confere no provedor sem gastar token. Com a chave
 * da concessionária, o consumo é medido e **não cobrado**: a conta da OpenAI é dela.
 *
 * **O consumo.** Dois lados, lado a lado: o que o cockpit mediu (`core.consumo`, os tokens
 * de cada passo do modelo) e o que a OpenAI cobrou (a API de custos e a de uso dela, com uma
 * chave Admin **só leitura**). Os dois diferem quando a conta tem outros usos — o projeto
 * filtra. **O saldo de créditos não tem API**: a tela compara o gasto com o orçamento que o
 * administrador define e manda para o Billing da OpenAI.
 *
 * As chaves não voltam da API: a tela diz só se estão guardadas. Campo vazio mantém.
 */
import { useCallback, useEffect, useState } from "react";

import { ErroDaApi, api } from "../api/cliente";
import type {
  ChaveEmUso,
  ConsumoDoModelo,
  ModeloNaTela,
  ModeloNovo,
  TesteDaChave,
} from "../api/tipos";
import {
  Badge,
  Botao,
  Cartao,
  Estado,
  Linha,
  Painel,
} from "../componentes/base";
import { Icone } from "../componentes/icones";
import { numeroLongo } from "../formato";

const ENTRADA =
  "min-h-8 w-full rounded-botao border border-linha bg-painel px-2 text-[11.5px] text-tinta";

const BILLING =
  "https://platform.openai.com/settings/organization/billing/overview";
const CHAVES_ADMIN =
  "https://platform.openai.com/settings/organization/admin-keys";

const CHAVE_EM_USO: Record<
  ChaveEmUso,
  { rotulo: string; cor: "verde" | "azul" | "cinza"; frase: string }
> = {
  organizacao: {
    rotulo: "Chave da concessionária",
    cor: "verde",
    frase:
      "As perguntas rodam na conta da OpenAI da concessionária. O consumo é medido e não cobrado.",
  },
  instalacao: {
    rotulo: "Chave da instalação",
    cor: "azul",
    frase:
      "Sem chave da concessionária, as perguntas rodam na chave desta instalação. Cole a chave dela para usar a conta dela.",
  },
  regras: {
    rotulo: "Regras, sem modelo",
    cor: "cinza",
    frase:
      "Nenhuma chave: as perguntas são entendidas pelas regras, sem modelo de linguagem — respondem as perguntas sugeridas, e só elas.",
  },
};

const SITUACAO: Record<string, string> = {
  cobrado: "Cobrado pela plataforma",
  chave_do_cliente: "Na chave da concessionária (não cobrado)",
  cortesia: "Regras, sem modelo (cortesia)",
  falha_nossa: "Falha nossa (não cobrado)",
};

const DOLAR = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "USD",
});
const DOLAR_FINO = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 4,
});

/** Os valores em dólar chegam como texto (`Decimal` no servidor). */
function dolares(valor: string | null | undefined, fino = false): string {
  if (valor === null || valor === undefined) return "—";
  const numero = Number(valor);
  if (!Number.isFinite(numero)) return "—";
  return (fino ? DOLAR_FINO : DOLAR).format(numero);
}

/**
 * A frase do erro. O 422 do Pydantic vem como lista (`detail[0].msg`), e o validador da
 * chave escreve a frase para quem está na tela — "Esta é uma chave Admin…" —, então ela
 * aparece inteira, sem o "Value error, " que o Pydantic põe na frente.
 */
function mensagem(falha: unknown): string {
  if (!(falha instanceof ErroDaApi)) return "Falha inesperada.";
  const detalhe = falha.detalhe as unknown;
  if (Array.isArray(detalhe) && detalhe.length > 0) {
    const primeiro = detalhe[0] as { msg?: unknown; loc?: unknown };
    if (typeof primeiro.msg === "string") {
      return primeiro.msg.replace(/^Value error, /, "");
    }
  }
  return falha.message;
}

// --------------------------------------------------------------------------- o modelo

export function ModeloDeLinguagem({ aoGravar }: { aoGravar?: () => void }) {
  const [modelo, definirModelo] = useState<ModeloNaTela | null>(null);
  const [erro, definirErro] = useState<string | null>(null);

  useEffect(() => {
    api<ModeloNaTela>("/whatsbi/modelo")
      .then(definirModelo)
      .catch((falha: unknown) => definirErro(mensagem(falha)));
  }, []);

  if (erro) {
    return (
      <Painel titulo="Modelo de linguagem">
        <Estado
          tipo="erro"
          titulo="Não foi possível carregar o modelo."
          detalhe={erro}
        />
      </Painel>
    );
  }
  if (!modelo) {
    return (
      <Painel titulo="Modelo de linguagem">
        <Estado tipo="carregando" titulo="Carregando…" />
      </Painel>
    );
  }
  const emUso = CHAVE_EM_USO[modelo.chave_em_uso];
  return (
    <Painel
      titulo="Quem entende as perguntas"
      acoes={<Badge cor={emUso.cor}>{emUso.rotulo}</Badge>}
    >
      <FormularioDoModelo
        // A chave reinicia o formulário com o que voltou do servidor depois de gravar.
        key={JSON.stringify(modelo)}
        modelo={modelo}
        aoGravar={(novo) => {
          definirModelo(novo);
          aoGravar?.();
        }}
      />
    </Painel>
  );
}

function Campo({
  rotulo,
  valor,
  aoMudar,
  placeholder,
  segredo,
  dica,
}: {
  rotulo: string;
  valor: string;
  aoMudar: (v: string) => void;
  placeholder?: string | undefined;
  segredo?: boolean | undefined;
  dica?: string | undefined;
}) {
  return (
    <label className="grid gap-1 text-[11px] text-tinta-3">
      {rotulo}
      <input
        value={valor}
        aria-label={rotulo}
        type={segredo ? "password" : "text"}
        autoComplete="off"
        spellCheck={false}
        placeholder={placeholder}
        onChange={(e) => aoMudar(e.target.value)}
        className={ENTRADA}
      />
      {dica && <small className="text-[10.5px] text-tinta-3">{dica}</small>}
    </label>
  );
}

function Apagar({
  rotulo,
  marcado,
  aoMudar,
}: {
  rotulo: string;
  marcado: boolean;
  aoMudar: (v: boolean) => void;
}) {
  return (
    <label className="flex items-center gap-1.5 text-[11px] text-tinta-3">
      <input
        type="checkbox"
        checked={marcado}
        onChange={(e) => aoMudar(e.target.checked)}
      />
      {rotulo}
    </label>
  );
}

function FormularioDoModelo({
  modelo,
  aoGravar,
}: {
  modelo: ModeloNaTela;
  aoGravar: (novo: ModeloNaTela) => void;
}) {
  const [dados, definirDados] = useState({
    chave: "",
    apagar_chave: false,
    modelo: modelo.modelo ?? "",
    chave_admin: "",
    apagar_chave_admin: false,
    projeto: modelo.projeto ?? "",
    orcamento: modelo.orcamento_mensal_usd
      ? String(Number(modelo.orcamento_mensal_usd)).replace(".", ",")
      : "",
  });
  const [ocupado, definirOcupado] = useState(false);
  const [aviso, definirAviso] = useState<{ ok: boolean; texto: string } | null>(
    null,
  );

  const mudar =
    <K extends keyof typeof dados>(campo: K) =>
    (valor: (typeof dados)[K]) =>
      definirDados((d) => ({ ...d, [campo]: valor }));

  async function gravar() {
    definirOcupado(true);
    definirAviso(null);
    const orcamento = dados.orcamento
      .trim()
      .replace(/\./g, "")
      .replace(",", ".");
    const corpo: ModeloNovo = {
      chave: dados.chave.trim() || null,
      apagar_chave: dados.apagar_chave,
      modelo: dados.modelo.trim() || null,
      chave_admin: dados.chave_admin.trim() || null,
      apagar_chave_admin: dados.apagar_chave_admin,
      projeto: dados.projeto.trim() || null,
      orcamento_mensal_usd: orcamento || null,
    };
    try {
      const novo = await api<ModeloNaTela>("/whatsbi/modelo", {
        method: "PUT",
        corpo,
      });
      aoGravar(novo);
    } catch (falha) {
      definirAviso({ ok: false, texto: mensagem(falha) });
    } finally {
      definirOcupado(false);
    }
  }

  async function testar() {
    definirOcupado(true);
    definirAviso(null);
    try {
      const teste = await api<TesteDaChave>("/whatsbi/modelo/testar", {
        method: "POST",
      });
      definirAviso({ ok: teste.ok, texto: teste.mensagem });
    } catch (falha) {
      definirAviso({ ok: false, texto: mensagem(falha) });
    } finally {
      definirOcupado(false);
    }
  }

  const padrao = modelo.modelo_da_instalacao;
  return (
    <div className="grid gap-3">
      <p className="text-[11.5px] text-tinta-2">
        {CHAVE_EM_USO[modelo.chave_em_uso].frase}
      </p>
      {!modelo.decifra && (
        <p
          role="alert"
          className="rounded-botao border border-amarelo-borda bg-amarelo-fundo px-2 py-1.5 text-[11px] text-amarelo"
        >
          Uma chave guardada não abre mais (o cofre desta instalação mudou de
          chave). Cole-a de novo.
        </p>
      )}
      <div className="grid gap-3 md:grid-cols-2">
        <Cartao titulo="A chave que faz as perguntas" icone={<Icone.Chave />}>
          <div className="grid gap-2">
            <Campo
              rotulo="Chave da API da OpenAI"
              valor={dados.chave}
              aoMudar={mudar("chave")}
              segredo
              placeholder={
                modelo.chave_guardada ? "guardada — vazio mantém" : "sk-proj-…"
              }
              dica="Uma chave de projeto da conta da concessionária. Fica cifrada e não volta para a tela."
            />
            {modelo.chave_guardada && (
              <Apagar
                rotulo="Apagar a chave guardada (volta para a da instalação)"
                marcado={dados.apagar_chave}
                aoMudar={mudar("apagar_chave")}
              />
            )}
            <Campo
              rotulo="Modelo"
              valor={dados.modelo}
              aoMudar={mudar("modelo")}
              placeholder={padrao ? `${padrao} (o da instalação)` : "gpt-…"}
              dica="Vazio usa o modelo padrão da instalação."
            />
          </div>
        </Cartao>

        <Cartao
          titulo="O gasto da conta (só leitura)"
          icone={<Icone.Grafico />}
        >
          <div className="grid gap-2">
            <Campo
              rotulo="Chave Admin só leitura"
              valor={dados.chave_admin}
              aoMudar={mudar("chave_admin")}
              segredo
              placeholder={
                modelo.chave_admin_guardada
                  ? "guardada — vazio mantém"
                  : "sk-admin-…"
              }
            />
            <small className="text-[10.5px] text-tinta-3">
              Crie em{" "}
              <a
                href={CHAVES_ADMIN}
                target="_blank"
                rel="noreferrer"
                className="text-azul-claro underline"
              >
                Admin keys
              </a>{" "}
              com permissão <b>só de leitura</b>: o cockpit lê o gasto e o uso,
              e não precisa (nem deve poder) mexer na conta.
            </small>
            {modelo.chave_admin_guardada && (
              <Apagar
                rotulo="Apagar a chave Admin guardada"
                marcado={dados.apagar_chave_admin}
                aoMudar={mudar("apagar_chave_admin")}
              />
            )}
            <div className="grid gap-2 sm:grid-cols-2">
              <Campo
                rotulo="Projeto da OpenAI (opcional)"
                valor={dados.projeto}
                aoMudar={mudar("projeto")}
                placeholder="proj_…"
                dica="Filtra o gasto quando a conta tem outros usos."
              />
              <Campo
                rotulo="Orçamento do mês (US$)"
                valor={dados.orcamento}
                aoMudar={mudar("orcamento")}
                placeholder="50,00"
                dica="Avisa aos 80% e ao passar."
              />
            </div>
          </div>
        </Cartao>
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        <Botao
          papel="primaria"
          disabled={ocupado}
          onClick={() => void gravar()}
        >
          Gravar
        </Botao>
        <Botao disabled={ocupado} onClick={() => void testar()}>
          Testar a chave em uso
        </Botao>
        {aviso && (
          <span
            role={aviso.ok ? "status" : "alert"}
            className={`text-[11px] ${aviso.ok ? "text-verde" : "text-vermelho"}`}
          >
            {aviso.texto}
          </span>
        )}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- o consumo

function mesDeHoje(): string {
  const hoje = new Date();
  return `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, "0")}`;
}

export function ConsumoDoMes({ versao = 0 }: { versao?: number }) {
  const [mes, definirMes] = useState(mesDeHoje);
  const [consumo, definirConsumo] = useState<ConsumoDoModelo | null>(null);
  const [erro, definirErro] = useState<string | null>(null);
  const [buscando, definirBuscando] = useState(false);

  const carregar = useCallback(
    async (fresco: boolean) => {
      definirBuscando(true);
      try {
        const consulta = new URLSearchParams({ mes });
        if (fresco) consulta.set("fresco", "true");
        definirConsumo(
          await api<ConsumoDoModelo>(`/whatsbi/consumo?${consulta.toString()}`),
        );
        definirErro(null);
      } catch (falha) {
        definirErro(mensagem(falha));
      } finally {
        definirBuscando(false);
      }
    },
    [mes],
  );

  useEffect(() => {
    void carregar(false);
  }, [carregar, versao]);

  return (
    <Painel
      titulo={`Consumo do modelo${consumo ? ` — ${consumo.rotulo}` : ""}`}
      acoes={
        <span className="flex flex-wrap items-center gap-1.5">
          <input
            type="month"
            aria-label="Mês"
            value={mes}
            max={mesDeHoje()}
            onChange={(e) => e.target.value && definirMes(e.target.value)}
            className="min-h-8 rounded-botao border border-linha bg-painel px-2 text-[11.5px] text-tinta"
          />
          <Botao disabled={buscando} onClick={() => void carregar(true)}>
            <Icone.Recarga />
            Atualizar da OpenAI
          </Botao>
        </span>
      }
    >
      {erro ? (
        <Estado
          tipo="erro"
          titulo="Não foi possível carregar o consumo."
          detalhe={erro}
        />
      ) : !consumo ? (
        <Estado tipo="carregando" titulo="Carregando…" />
      ) : (
        <Consumo consumo={consumo} />
      )}
    </Painel>
  );
}

function Consumo({ consumo }: { consumo: ConsumoDoModelo }) {
  const { cockpit, openai } = consumo;
  const passou = (consumo.percentual_do_orcamento ?? 0) >= 100;
  return (
    <div className="grid gap-3">
      {consumo.alerta && (
        <p
          role="alert"
          className={`rounded-botao border px-2.5 py-1.5 text-[11.5px] font-semibold ${
            passou
              ? "border-vermelho-borda bg-vermelho-fundo text-vermelho"
              : "border-amarelo-borda bg-amarelo-fundo text-amarelo"
          }`}
        >
          {consumo.alerta}
        </p>
      )}

      <dl className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <Numero rotulo="Perguntas" valor={numeroLongo(cockpit.perguntas)} />
        <Numero rotulo="Passos do modelo" valor={numeroLongo(cockpit.passos)} />
        <Numero rotulo="Tokens medidos" valor={numeroLongo(cockpit.tokens)} />
        <Numero
          rotulo="Gasto na OpenAI"
          valor={openai.disponivel ? dolares(openai.gasto_usd) : "—"}
        />
      </dl>

      {consumo.orcamento_mensal_usd && openai.disponivel && (
        <Orcamento
          gasto={openai.gasto_usd}
          orcamento={consumo.orcamento_mensal_usd}
          percentual={consumo.percentual_do_orcamento ?? 0}
        />
      )}

      <div className="grid gap-3 md:grid-cols-2">
        <Cartao titulo="Medido pelo cockpit" icone={<Icone.Grafico />}>
          <BarrasDoMes
            mes={consumo.mes}
            pontos={cockpit.por_dia.map((d) => ({
              dia: d.dia,
              valor: d.tokens,
            }))}
            descricao="Tokens por dia, medidos pelo cockpit"
            formatar={(v) => `${numeroLongo(v)} tokens`}
            cor="fill-azul"
          />
          {cockpit.por_situacao.length > 0 && (
            <div className="grid gap-0.5" aria-label="Na fatura">
              {cockpit.por_situacao.map((s) => (
                <Linha
                  key={s.situacao}
                  rotulo={SITUACAO[s.situacao] ?? s.situacao}
                  mono
                >
                  {numeroLongo(s.tokens)} tokens · {numeroLongo(s.passos)}{" "}
                  passos
                </Linha>
              ))}
            </div>
          )}
          {cockpit.por_modelo.length > 0 && (
            <div className="grid gap-0.5" aria-label="Por modelo, no cockpit">
              {cockpit.por_modelo.map((m) => (
                <Linha key={m.modelo} rotulo={m.modelo} mono>
                  {numeroLongo(m.tokens)} tokens
                </Linha>
              ))}
            </div>
          )}
        </Cartao>

        <Cartao titulo="Cobrado pela OpenAI" icone={<Icone.Grafico />}>
          {openai.disponivel ? (
            <>
              <BarrasDoMes
                mes={consumo.mes}
                pontos={openai.por_dia.map((d) => ({
                  dia: d.dia,
                  valor: Number(d.usd),
                }))}
                descricao="Gasto por dia na OpenAI, em dólares"
                formatar={(v) => DOLAR_FINO.format(v)}
                cor="fill-azul-claro"
              />
              {openai.projeto && (
                <p className="text-[10.5px] text-tinta-3">
                  Só o projeto <span className="mono">{openai.projeto}</span>.
                </p>
              )}
              {openai.por_modelo.length > 0 && (
                <div
                  className="grid gap-0.5"
                  aria-label="Por modelo, na OpenAI"
                >
                  {openai.por_modelo.map((m) => (
                    <Linha key={m.modelo} rotulo={m.modelo} mono>
                      {numeroLongo(m.tokens_de_entrada + m.tokens_de_saida)}{" "}
                      tokens · {numeroLongo(m.requisicoes)} req.
                    </Linha>
                  ))}
                </div>
              )}
            </>
          ) : (
            <p className="text-[11.5px] text-tinta-3">{openai.motivo}</p>
          )}
          <p className="text-[10.5px] text-tinta-3">
            A OpenAI não publica o saldo de créditos por API: veja no{" "}
            <a
              href={BILLING}
              target="_blank"
              rel="noreferrer"
              className="text-azul-claro underline"
            >
              Billing da OpenAI
            </a>
            .
          </p>
        </Cartao>
      </div>
    </div>
  );
}

function Numero({ rotulo, valor }: { rotulo: string; valor: string }) {
  return (
    <div className="grid gap-0.5 rounded-cartao border border-linha bg-painel-2 px-2.5 py-2">
      <dt className="text-titulo-cartao font-bold tracking-[0.07em] text-tinta-3 uppercase">
        {rotulo}
      </dt>
      <dd className="mono text-[20px] font-extrabold text-tinta">{valor}</dd>
    </div>
  );
}

function Orcamento({
  gasto,
  orcamento,
  percentual,
}: {
  gasto: string | null;
  orcamento: string;
  percentual: number;
}) {
  const cor =
    percentual >= 100
      ? "bg-vermelho"
      : percentual >= 80
        ? "bg-amarelo"
        : "bg-azul";
  return (
    <div className="grid gap-1">
      <div className="flex flex-wrap items-baseline justify-between gap-2 text-[11px] text-tinta-3">
        <span>Orçamento do mês</span>
        <span className="mono text-tinta-2">
          {dolares(gasto)} de {dolares(orcamento)} ·{" "}
          {percentual.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%
        </span>
      </div>
      <div
        role="meter"
        aria-label="Orçamento do mês"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.min(100, percentual)}
        className="h-2 overflow-hidden rounded-pilula border border-linha bg-painel"
      >
        <div
          className={`h-full ${cor}`}
          style={{ width: `${Math.min(100, percentual)}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Barras do mês, um dia por barra, do dia 1 ao último — dia sem consumo é barra vazia, e
 * não some: um mês com buracos tem de parecer um mês com buracos. Não é recorte (§4.1): não
 * há fila atrás de um token, e cada barra traz o número no `<title>`.
 */
function BarrasDoMes({
  mes,
  pontos,
  descricao,
  formatar,
  cor,
}: {
  mes: string;
  pontos: { dia: string; valor: number }[];
  descricao: string;
  formatar: (v: number) => string;
  cor: string;
}) {
  const [ano, numero] = mes.split("-").map(Number) as [number, number];
  const dias = new Date(ano, numero, 0).getDate();
  const valores = new Array<number>(dias).fill(0);
  for (const p of pontos) {
    const d = Number(p.dia.slice(8, 10));
    if (d >= 1 && d <= dias) valores[d - 1] = p.valor;
  }
  const maximo = Math.max(...valores);
  if (maximo <= 0) {
    return <p className="text-[11.5px] text-tinta-3">Nada neste mês ainda.</p>;
  }
  const total = valores.reduce((a, b) => a + b, 0);
  const faixa = 14;
  const A = 72;
  return (
    <figure className="grid gap-1">
      <svg
        viewBox={`0 0 ${dias * faixa} ${A + 14}`}
        width={dias * faixa}
        height={A + 14}
        className="block max-w-full"
        role="img"
        aria-label={`${descricao}: ${formatar(total)} no mês, o maior dia com ${formatar(maximo)}.`}
      >
        {valores.map((v, i) => {
          const altura = v > 0 ? Math.max(2, (v / maximo) * A) : 0;
          return (
            <g key={i}>
              <rect
                x={i * faixa + 2}
                y={A - altura}
                width={faixa - 4}
                height={altura}
                rx={1.5}
                className={cor}
              >
                <title>{`${String(i + 1).padStart(2, "0")}/${String(numero).padStart(2, "0")} — ${formatar(v)}`}</title>
              </rect>
              {(i === 0 || (i + 1) % 5 === 0) && (
                <text
                  x={i * faixa + faixa / 2}
                  y={A + 11}
                  textAnchor="middle"
                  className="fill-tinta-3 text-[8.5px]"
                >
                  {i + 1}
                </text>
              )}
            </g>
          );
        })}
      </svg>
      <figcaption className="flex justify-between text-[10.5px] text-tinta-3">
        <span>{descricao}</span>
        <span className="mono">máx. {formatar(maximo)}</span>
      </figcaption>
    </figure>
  );
}
